/**
 * Centralized field mappings for all search modules
 * Defines which URL filter fields map to API parameters and enriched data keys
 *
 * Each module has:
 * - fieldToApiMapping: URL field names -> API request parameter names
 * - apiToEnrichedMapping: API response keys -> enriched data state keys
 * - fieldToEnrichedMapping: URL field names -> enriched data state keys
 * - initialEnrichedData: Initial state with empty arrays for each enriched key
 * - textFilters: Fields that store text values (no enrichment needed)
 * - riskFilters: Fields that store numeric risk levels 1-5 (mapped via useFilterLabels)
 */

// ─── Active Substances ──────────────────────────────────────────────────────

export const ACTIVE_SUBSTANCE_FIELD_TO_API = {
  cultures: 'culture_ids',
  activity_sectors: 'activity_sector_ids',
  cultures_practices: 'cultural_practice_ids',
  chemical_families: 'chemical_family_ids',
  pesticide_groups: 'pesticide_group_ids',
  pesticide_categories: 'pesticide_category_ids',
  pesticide_types: 'pesticide_type_ids',
  activity_types: 'activity_type_ids',
  arla_culture_groups: 'arla_culture_group_ids',
  resistance: 'resistance_ids',
};

export const ACTIVE_SUBSTANCE_API_TO_ENRICHED = {
  cultures: 'cultures',
  activity_sectors: 'activitySectors',
  cultural_practices: 'culturalPractices',
  chemical_families: 'chemicalFamilies',
  pesticide_groups: 'pesticideGroups',
  pesticide_categories: 'pesticideCategories',
  pesticide_types: 'pesticideTypes',
  activity_types: 'activityTypes',
  arla_culture_groups: 'arlaCultureGroups',
  resistances: 'resistances',
};

export const ACTIVE_SUBSTANCE_FIELD_TO_ENRICHED = {
  cultures: 'cultures',
  activity_sectors: 'activitySectors',
  cultures_practices: 'culturalPractices',
  chemical_families: 'chemicalFamilies',
  pesticide_groups: 'pesticideGroups',
  pesticide_categories: 'pesticideCategories',
  pesticide_types: 'pesticideTypes',
  activity_types: 'activityTypes',
  arla_culture_groups: 'arlaCultureGroups',
  resistance: 'resistances',
};

export const ACTIVE_SUBSTANCE_INITIAL_ENRICHED = {
  cultures: [],
  activitySectors: [],
  culturalPractices: [],
  chemicalFamilies: [],
  pesticideGroups: [],
  pesticideCategories: [],
  pesticideTypes: [],
  activityTypes: [],
  arlaCultureGroups: [],
  resistances: [],
};

/** Text-only filters that don't need enrichment (stored as plain strings in URL) */
export const ACTIVE_SUBSTANCE_TEXT_FILTERS = ['names', 'cas_numbers'];

// ─── Commercial Products ────────────────────────────────────────────────────

export const PRODUCT_FIELD_TO_API = {
  cultures: 'culture_ids',
  cultures_practices: 'cultural_practice_ids',
  enemies: 'enemy_ids',
  activity_sectors: 'activity_sector_ids',
  product_types: 'product_type_ids',
  chemical_families: 'chemical_family_ids',
  pesticide_groups: 'pesticide_group_ids',
  pesticide_categories: 'pesticide_category_ids',
  activity_types: 'activity_type_ids',
  arla_culture_groups: 'arla_culture_group_ids',
  application_types: 'application_type_ids',
  allergens: 'allergen_ids',
};

export const PRODUCT_API_TO_ENRICHED = {
  cultures: 'cultures',
  cultural_practices: 'culturalPractices',
  enemies: 'enemies',
  activity_sectors: 'activitySectors',
  product_types: 'productTypes',
  chemical_families: 'chemicalFamilies',
  pesticide_groups: 'pesticideGroups',
  pesticide_categories: 'pesticideCategories',
  activity_types: 'activityTypes',
  arla_culture_groups: 'arlaCultureGroups',
  application_types: 'applicationTypes',
  allergens: 'allergens',
};

export const PRODUCT_FIELD_TO_ENRICHED = {
  cultures: 'cultures',
  cultures_practices: 'culturalPractices',
  enemies: 'enemies',
  activity_sectors: 'activitySectors',
  product_types: 'productTypes',
  chemical_families: 'chemicalFamilies',
  pesticide_groups: 'pesticideGroups',
  pesticide_categories: 'pesticideCategories',
  activity_types: 'activityTypes',
  arla_culture_groups: 'arlaCultureGroups',
  application_types: 'applicationTypes',
  allergens: 'allergens',
};

export const PRODUCT_INITIAL_ENRICHED = {
  cultures: [],
  culturalPractices: [],
  enemies: [],
  activitySectors: [],
  productTypes: [],
  chemicalFamilies: [],
  pesticideGroups: [],
  pesticideCategories: [],
  activityTypes: [],
  arlaCultureGroups: [],
  applicationTypes: [],
  allergens: [],
};

/** Text-only filters that don't need enrichment */
export const PRODUCT_TEXT_FILTERS = [
  'product_names',
  'active_substance_names',
  'commercial_packagings',
  'registration_numbers',
];

/** Boolean filter */
export const PRODUCT_BOOLEAN_FILTERS = ['organic_approval'];

// ─── Treatment Search ───────────────────────────────────────────────────────

export const TREATMENT_FIELD_TO_API = {
  cultures: 'culture_ids',
  cultural_practices: 'cultural_practice_ids',
  treatment_types: 'treatment_type_ids',
  application_types: 'application_type_ids',
  intervention_periods: 'intervention_period_ids',
  enemies: 'enemy_ids',
  commercial_products: 'commercial_product_ids',
  active_substances: 'active_substance_ids',
  pesticide_groups: 'pesticide_group_ids',
  culture_stages: 'culture_stage_ids',
  soil_types: 'soil_type_ids',
  intercrop_cultures: 'intercrop_culture_ids',
};

export const TREATMENT_API_TO_ENRICHED = {
  cultures: 'cultures',
  cultural_practices: 'culturalPractices',
  treatment_types: 'treatmentTypes',
  application_types: 'applicationTypes',
  intervention_periods: 'interventionPeriods',
  enemies: 'enemies',
  commercial_products: 'commercialProducts',
  active_substances: 'activeSubstances',
  pesticide_groups: 'pesticideGroups',
  culture_stages: 'cultureStages',
  soil_types: 'soilTypes',
  intercrop_cultures: 'intercropCultures',
};

export const TREATMENT_FIELD_TO_ENRICHED = {
  cultures: 'cultures',
  cultural_practices: 'culturalPractices',
  treatment_types: 'treatmentTypes',
  application_types: 'applicationTypes',
  intervention_periods: 'interventionPeriods',
  enemies: 'enemies',
  commercial_products: 'commercialProducts',
  active_substances: 'activeSubstances',
  pesticide_groups: 'pesticideGroups',
  culture_stages: 'cultureStages',
  soil_types: 'soilTypes',
  intercrop_cultures: 'intercropCultures',
};

export const TREATMENT_INITIAL_ENRICHED = {
  cultures: [],
  culturalPractices: [],
  treatmentTypes: [],
  applicationTypes: [],
  interventionPeriods: [],
  enemies: [],
  commercialProducts: [],
  activeSubstances: [],
  pesticideGroups: [],
  cultureStages: [],
  soilTypes: [],
  intercropCultures: [],
};

// ─── Shared Risk Filters ────────────────────────────────────────────────────

/** Risk symbol filters shared across all modules (store numeric values 1-5) */
export const RISK_FILTERS = [
  'acute_toxicity',
  'long_term_effects',
  'fish_or_daphnia',
  'birds',
  'bees',
  'persistence',
  'leaching_potential',
];
